export type MenuShortcut = {
  linux?: string;
  macos?: string;
  shortcut?: string;
  windows?: string;
};

type Platform = "linux" | "macos" | "windows";

type ParsedShortcut = {
  alt: boolean;
  ctrl: boolean;
  key: string;
  meta: boolean;
  shift: boolean;
};

const modifierAliases: Record<string, keyof Omit<ParsedShortcut, "key"> | "primary"> = {
  alt: "alt",
  cmd: "primary",
  command: "primary",
  control: "ctrl",
  ctrl: "ctrl",
  meta: "meta",
  mod: "primary",
  option: "alt",
  primary: "primary",
  shift: "shift",
  win: "meta",
  "⌘": "primary",
  "⌃": "ctrl",
  "⌥": "alt",
  "⇧": "shift",
};

const keyAliases: Record<string, string> = {
  " ": " ",
  "⌫": "backspace",
  "⌦": "delete",
  "↵": "enter",
  "⎋": "escape",
  "⇥": "tab",
  "←": "arrowleft",
  "↑": "arrowup",
  "→": "arrowright",
  "↓": "arrowdown",
  backspace: "backspace",
  delete: "delete",
  down: "arrowdown",
  enter: "enter",
  esc: "escape",
  escape: "escape",
  left: "arrowleft",
  right: "arrowright",
  space: " ",
  tab: "tab",
  up: "arrowup",
};

const macModifierLabels: Record<string, string> = {
  alt: "⌥",
  cmd: "⌘",
  command: "⌘",
  control: "⌃",
  ctrl: "⌃",
  meta: "⌘",
  mod: "⌘",
  option: "⌥",
  primary: "⌘",
  shift: "⇧",
  "⌘": "⌘",
  "⌃": "⌃",
  "⌥": "⌥",
  "⇧": "⇧",
};

const defaultModifierLabels: Record<string, string> = {
  alt: "Alt",
  cmd: "Ctrl",
  command: "Ctrl",
  control: "Ctrl",
  ctrl: "Ctrl",
  meta: "Ctrl",
  mod: "Ctrl",
  option: "Alt",
  primary: "Ctrl",
  shift: "Shift",
  win: "Win",
  "⌘": "Ctrl",
  "⌃": "Ctrl",
  "⌥": "Alt",
  "⇧": "Shift",
};

const keyLabels: Record<string, { default: string; macos: string }> = {
  " ": { default: "Space", macos: "␣" },
  arrowdown: { default: "↓", macos: "↓" },
  arrowleft: { default: "←", macos: "←" },
  arrowright: { default: "→", macos: "→" },
  arrowup: { default: "↑", macos: "↑" },
  backspace: { default: "Backspace", macos: "⌫" },
  delete: { default: "Delete", macos: "⌦" },
  enter: { default: "Enter", macos: "↵" },
  escape: { default: "Esc", macos: "⎋" },
  tab: { default: "Tab", macos: "⇥" },
};

export function formatMenuShortcut(shortcut: MenuShortcut | string | undefined) {
  const resolved = resolveMenuShortcut(shortcut);
  if (!resolved) {
    return undefined;
  }

  const platform = currentPlatform();
  const modifierLabels = platform === "macos" ? macModifierLabels : defaultModifierLabels;
  const parts = shortcutTokens(resolved).map((token) => {
    const lowerToken = token.toLowerCase();
    if (modifierAliases[lowerToken] || modifierAliases[token]) {
      return modifierLabels[lowerToken] ?? modifierLabels[token] ?? token;
    }

    const normalizedKey = normalizeKey(token);
    const label = keyLabels[normalizedKey];
    if (label) {
      return platform === "macos" ? label.macos : label.default;
    }

    return token.length === 1 ? token.toUpperCase() : token;
  });

  return platform === "macos" ? parts.join("") : parts.join("+");
}

export function matchesMenuShortcut(event: KeyboardEvent | React.KeyboardEvent, shortcut: MenuShortcut | string | undefined) {
  const resolved = resolveMenuShortcut(shortcut);
  const parsed = resolved ? parseShortcut(resolved) : null;
  if (!parsed) {
    return false;
  }

  return (
    event.key.toLowerCase() === parsed.key &&
    event.altKey === parsed.alt &&
    event.ctrlKey === parsed.ctrl &&
    event.metaKey === parsed.meta &&
    event.shiftKey === parsed.shift
  );
}

export function resolveMenuShortcut(shortcut: MenuShortcut | string | undefined) {
  if (!shortcut) {
    return undefined;
  }
  if (typeof shortcut === "string") {
    return shortcut;
  }

  return shortcut[currentPlatform()] ?? shortcut.shortcut;
}

function parseShortcut(shortcut: string): ParsedShortcut | null {
  const parsed: ParsedShortcut = { alt: false, ctrl: false, key: "", meta: false, shift: false };
  for (const token of shortcutTokens(shortcut)) {
    const lowerToken = token.toLowerCase();
    const modifier = modifierAliases[lowerToken] ?? modifierAliases[token];
    if (modifier) {
      const actualModifier = modifier === "primary" ? primaryModifier() : modifier;
      parsed[actualModifier] = true;
      continue;
    }

    parsed.key = normalizeKey(token);
  }

  return parsed.key ? parsed : null;
}

function currentPlatform(): Platform {
  if (typeof navigator === "undefined") {
    return "macos";
  }

  const platform = navigator.platform.toLowerCase();
  const userAgent = navigator.userAgent.toLowerCase();
  if (platform.includes("mac") || userAgent.includes("mac os")) {
    return "macos";
  }
  if (platform.includes("win") || userAgent.includes("windows")) {
    return "windows";
  }
  return "linux";
}

function normalizeKey(token: string) {
  return keyAliases[token] ?? keyAliases[token.toLowerCase()] ?? token.toLowerCase();
}

function primaryModifier(): "ctrl" | "meta" {
  return currentPlatform() === "macos" ? "meta" : "ctrl";
}

function shortcutTokens(shortcut: string) {
  const normalized = shortcut.trim().replace(/\s*\+\s*/g, "+");
  return normalized.includes("+") ? normalized.split("+").filter(Boolean) : [...normalized].filter((token) => token.trim());
}
